"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import WaveDropReactions from "@/components/waves/drops/WaveDropReactions";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation, hasDropFooter } from "@/components/waves/drops/drop.types";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useCallback, type MouseEvent, type ReactNode } from "react";
import MemeDropActions from "./meme-participation-drop/MemeDropActions";
import MemeDropArtistInfo from "./meme-participation-drop/MemeDropArtistInfo";
import MemeDropDescription from "./meme-participation-drop/MemeDropDescription";
import MemeDropHeader from "./meme-participation-drop/MemeDropHeader";
import MemeDropVoteStats from "./meme-participation-drop/MemeDropVoteStats";
import MemeDropTraits from "./MemeDropTraits";
interface MemeParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: ReactNode;
  readonly showInteractions?: boolean | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
const getNonEmptyText = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text && text.length > 0 ? text : null;
};
const getMetadataValue = (drop: ExtendedDrop, dataKey: string): string | null => getNonEmptyText(drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);

// Border styling based on rank
const getBorderClasses = (drop: ExtendedDrop, isActiveDrop: boolean) => {
  const rank = typeof drop.rank === "number" && drop.rank <= 3 ? drop.rank : null;
  const baseClasses = "tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-transition-all tw-duration-200 tw-ease-out tw-overflow-hidden";
  if (isActiveDrop) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#3CCB7F]/40 tw-bg-[#3CCB7F]/5`;
  }
  if (rank === 1) {
    return `${baseClasses} ${getRankHoverBorderClass(1)}`;
  }
  if (rank === 2) {
    return `${baseClasses} ${getRankHoverBorderClass(2)}`;
  }
  if (rank === 3) {
    return `${baseClasses} ${getRankHoverBorderClass(3)}`;
  }
  return `${baseClasses} tw-border-iron-800`;
};
export default function MemeParticipationDrop(t0) {
  const $ = _c(86);
  const {
    drop,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onDropContentClick,
    footer,
    showInteractions: t1,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const {
    isOpen: isVotingModalOpen,
    open: openVotingModal,
    close: closeVotingModal
  } = useVotingModalState(isVotingActionLocked);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  let description;
  let t4;
  let title;
  if ($[0] !== drop) {
    const firstPart = drop.parts.at(0);
    title = getNonEmptyText(drop.title) ?? getMetadataValue(drop, "title") ?? "Artwork Title";
    description = getNonEmptyText(firstPart?.content) ?? getMetadataValue(drop, "description") ?? "This is an artwork submission for The Memes collection.";
    t4 = firstPart?.media.at(0);
    $[0] = drop;
    $[1] = description;
    $[2] = t4;
    $[3] = title;
  } else {
    description = $[1];
    t4 = $[2];
    title = $[3];
  }
  const artworkMedia = t4;
  let t5;
  if ($[4] !== drop || $[5] !== isActiveDrop) {
    t5 = getBorderClasses(drop, isActiveDrop);
    $[4] = drop;
    $[5] = isActiveDrop;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const borderClasses = t5;
  let t6;
  if ($[7] !== drop || $[8] !== onReply) {
    t6 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[7] = drop;
    $[8] = onReply;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleOnReply = t6;
  let t7;
  if ($[10] !== drop.id || $[11] !== onDropContentClick) {
    t7 = !drop.id.startsWith("temp-") && !!onDropContentClick;
    $[10] = drop.id;
    $[11] = onDropContentClick;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const isContentInteractive = t7;
  let t8;
  if ($[13] !== drop || $[14] !== isContentInteractive || $[15] !== onDropContentClick) {
    t8 = event => {
      const selection = globalThis.getSelection?.() ?? null;
      if (selection?.toString()) {
        return;
      }
      if (!isContentInteractive || !onDropContentClick) {
        return;
      }
      event.stopPropagation();
      onDropContentClick(drop);
    };
    $[13] = drop;
    $[14] = isContentInteractive;
    $[15] = onDropContentClick;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleContentClick = t8;
  let t9;
  if ($[17] !== drop) {
    t9 = <MemeDropArtistInfo drop={drop} />;
    $[17] = drop;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== description || $[20] !== handleContentClick || $[21] !== isContentInteractive || $[22] !== title) {
    t10 = isContentInteractive ? <button className="tw-mt-2 tw-block tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-inherit focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 sm:tw-ml-[3.25rem] sm:tw-mt-1.5 sm:tw-w-auto" onClick={handleContentClick} type="button"><MemeDropHeader title={title} /><MemeDropDescription description={description} /></button> : <div className="tw-mt-2 tw-flex tw-flex-col sm:tw-ml-[3.25rem] sm:tw-mt-1.5"><MemeDropHeader title={title} /><MemeDropDescription description={description} /></div>;
    $[19] = description;
    $[20] = handleContentClick;
    $[21] = isContentInteractive;
    $[22] = title;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div className="tw-p-4">{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== artworkMedia || $[28] !== location) {
    t12 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-800/30" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[27] = artworkMedia;
    $[28] = location;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== drop) {
    t13 = <div className="tw-px-4 tw-py-4"><MemeDropTraits drop={drop} /></div>;
    $[30] = drop;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    const contentBody = <>{t11}{t12}{t13}</>;
    t14 = <div>{contentBody}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const content = t14;
  const t15 = `tw-group tw-relative tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  const t16 = `${borderClasses} ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"}`;
  let t17;
  if ($[36] !== content || $[37] !== drop || $[38] !== handleOnReply || $[39] !== showInteractions || $[40] !== showReplyAndQuote) {
    t17 = showInteractions ? <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{content}</DropMobileMenuHandler> : content;
    $[36] = content;
    $[37] = drop;
    $[38] = handleOnReply;
    $[39] = showInteractions;
    $[40] = showReplyAndQuote;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== drop.context_profile_context || $[43] !== drop.raters_count || $[44] !== drop.rating || $[45] !== drop.rating_prediction || $[46] !== drop.top_raters || $[47] !== drop.wave.voting_credit_type) {
    t18 = <div className="tw-px-4 @[700px]:tw-px-0"><MemeDropVoteStats current={drop.rating} projected={drop.rating_prediction} votingCreditType={drop.wave.voting_credit_type} ratersCount={drop.raters_count} topVoters={drop.top_raters} userContext={drop.context_profile_context} /></div>;
    $[42] = drop.context_profile_context;
    $[43] = drop.raters_count;
    $[44] = drop.rating;
    $[45] = drop.rating_prediction;
    $[46] = drop.top_raters;
    $[47] = drop.wave.voting_credit_type;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== canShowVote || $[50] !== drop || $[51] !== isVotingActionLocked || $[52] !== openVotingModal || $[53] !== showInteractions) {
    t19 = canShowVote && showInteractions && !isVotingActionLocked && <div className="tw-flex tw-w-full tw-justify-center tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-px-6 tw-pt-4 @[700px]:tw-w-auto @[700px]:tw-border-none @[700px]:tw-px-0 @[700px]:tw-pt-0"><div onClick={_temp}><VotingModalButton drop={drop} onClick={openVotingModal} /></div></div>;
    $[49] = canShowVote;
    $[50] = drop;
    $[51] = isVotingActionLocked;
    $[52] = openVotingModal;
    $[53] = showInteractions;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== t18 || $[56] !== t19) {
    t20 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-3 tw-pb-4 @[700px]:tw-flex-row @[700px]:tw-items-center @[700px]:tw-gap-y-0 @[700px]:tw-px-4">{t18}{t19}</div>;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== drop || $[59] !== showInteractions) {
    t21 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-px-4 tw-pb-4"><WaveDropReactions drop={drop} /></div>;
    $[58] = drop;
    $[59] = showInteractions;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== footer) {
    t22 = hasDropFooter(footer) && <div className="tw-px-4 tw-pb-4 tw-pt-2">{footer}</div>;
    $[61] = footer;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== drop || $[64] !== handleOnReply || $[65] !== isMobile || $[66] !== showInteractions || $[67] !== showReplyAndQuote) {
    t23 = showInteractions && <div className="tw-absolute tw-right-4 tw-top-2"><MemeDropActions drop={drop} isMobile={isMobile} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply} /></div>;
    $[63] = drop;
    $[64] = handleOnReply;
    $[65] = isMobile;
    $[66] = showInteractions;
    $[67] = showReplyAndQuote;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t16 || $[70] !== t17 || $[71] !== t20 || $[72] !== t21 || $[73] !== t22 || $[74] !== t23) {
    t24 = <div className={t16}>{t17}{t20}{t21}{t22}{t23}</div>;
    $[69] = t16;
    $[70] = t17;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
    $[74] = t23;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== closeVotingModal || $[77] !== drop || $[78] !== isMobileScreen || $[79] !== isVotingModalOpen || $[80] !== showInteractions) {
    t25 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={closeVotingModal} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={closeVotingModal} />);
    $[76] = closeVotingModal;
    $[77] = drop;
    $[78] = isMobileScreen;
    $[79] = isVotingModalOpen;
    $[80] = showInteractions;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  let t26;
  if ($[82] !== t15 || $[83] !== t24 || $[84] !== t25) {
    t26 = <div className="tw-w-full tw-@container"><div className={t15}>{t24}{t25}</div></div>;
    $[82] = t15;
    $[83] = t24;
    $[84] = t25;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  return t26;
}
function _temp(e) {
  return e.stopPropagation();
}